import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LogOut, RefreshCcw, Trash2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKEN_KEY = "mf_admin_token";

const StatusBadge = ({ status }) => {
  const map = {
    new: "bg-[#dc2626] text-white",
    contacted: "bg-[#fbbf24] text-black",
    closed: "bg-[#22c55e] text-black",
  };
  return (
    <span
      className={`font-mono text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${map[status] || "bg-[#27272a] text-white"}`}
    >
      {status}
    </span>
  );
};

const Login = ({ onLogin }) => {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { token });
      localStorage.setItem(TOKEN_KEY, token);
      onLogin(token);
      toast.success("Zalogowano");
    } catch (err) {
      toast.error("Nieprawidłowy token");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-[#0e0e0e] border border-[#1f1f1f] p-8 md:p-10"
      >
        <div className="flex items-center gap-3 mb-2">
          <span className="w-10 h-px bg-[#dc2626]" />
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#dc2626]">
            Admin
          </p>
        </div>
        <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl mb-6">
          Panel zapytań
        </h1>
        <p className="text-sm text-[#a1a1aa] mb-6">
          Wprowadź token administratora. Domyślny token można zmienić w pliku{" "}
          <span className="font-mono text-[#dc2626]">backend/.env</span>{" "}
          (ADMIN_TOKEN).
        </p>
        <label className="block mb-6">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#a1a1aa] mb-2 inline-block">
            Token
          </span>
          <input
            type="password"
            data-testid="admin-token-input"
            autoFocus
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="w-full bg-[#0a0a0a] border border-[#27272a] text-white font-mono text-sm px-4 py-3.5 focus:outline-none focus:border-[#dc2626]"
          />
        </label>
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="w-full bg-[#dc2626] text-white font-bold uppercase tracking-wider text-xs px-7 py-4 hover:bg-[#b91c1c] disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
        >
          <ShieldCheck className="w-4 h-4" />
          {loading ? "..." : "Zaloguj"}
        </button>
      </form>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (tk = token) => {
    if (!tk) return;
    setLoading(true);
    try {
      const [listR, statsR] = await Promise.all([
        axios.get(`${API}/admin/inquiries`, {
          headers: { "X-Admin-Token": tk },
        }),
        axios.get(`${API}/admin/stats`, {
          headers: { "X-Admin-Token": tk },
        }),
      ]);
      setInquiries(listR.data);
      setStats(statsR.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Sesja wygasła");
      } else {
        toast.error("Błąd ładowania danych");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/inquiries/${id}`,
        { status },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success("Zaktualizowano");
      load();
    } catch (err) {
      toast.error("Błąd aktualizacji");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć zapytanie?")) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Usunięto");
      load();
    } catch (err) {
      toast.error("Błąd usuwania");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <Login onLogin={setToken} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="border-b border-[#1f1f1f] bg-[#0a0a0a]/90 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 bg-[#dc2626] text-white font-display font-black flex items-center justify-center">
              MF
            </span>
            <div>
              <p className="font-display font-bold uppercase tracking-tight text-sm">
                Panel administracyjny
              </p>
              <p className="font-mono text-[10px] tracking-[0.2em] text-[#71717a] uppercase">
                Zapytania ofertowe
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="admin-reload"
              onClick={() => load()}
              className="p-2 border border-[#27272a] hover:border-[#dc2626] hover:text-[#dc2626]"
              title="Odśwież"
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              type="button"
              data-testid="admin-logout"
              onClick={logout}
              className="p-2 border border-[#27272a] hover:border-[#dc2626] hover:text-[#dc2626]"
              title="Wyloguj"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-[#1f1f1f] mb-10">
            {[
              { k: "total", l: "Łącznie" },
              { k: "new", l: "Nowe" },
              { k: "contacted", l: "Kontakt" },
              { k: "closed", l: "Zamknięte" },
              { k: "b2b", l: "B2B" },
              { k: "retail", l: "Detal" },
            ].map((s) => (
              <div
                key={s.k}
                data-testid={`stat-${s.k}`}
                className="bg-[#0e0e0e] p-5"
              >
                <p className="font-display font-black text-3xl">{stats[s.k]}</p>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#71717a] mt-2">
                  {s.l}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Inquiries */}
        {inquiries.length === 0 ? (
          <div
            data-testid="admin-empty"
            className="border border-[#1f1f1f] bg-[#0e0e0e] p-10 text-center text-[#a1a1aa]"
          >
            Brak zapytań.
          </div>
        ) : (
          <div className="grid gap-4">
            {inquiries.map((q) => (
              <article
                key={q.id}
                data-testid={`inquiry-${q.id}`}
                className="border border-[#1f1f1f] bg-[#0e0e0e] p-6 md:p-8"
              >
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="font-display font-bold uppercase tracking-tight text-lg">
                        {q.name}
                      </h3>
                      <StatusBadge status={q.status} />
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#dc2626] border border-[#dc2626]/40 px-2 py-0.5">
                        {q.inquiry_type}
                      </span>
                    </div>
                    <p className="font-mono text-xs text-[#71717a]">
                      {q.company || "—"} · {q.email} · {q.phone || "brak tel."}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                      value={q.status}
                      onValueChange={(v) => updateStatus(q.id, v)}
                    >
                      <SelectTrigger
                        data-testid={`status-${q.id}`}
                        className="bg-[#0a0a0a] border-[#27272a] rounded-none h-9 text-xs font-mono uppercase tracking-wider w-[140px]"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#27272a] rounded-none">
                        <SelectItem value="new" className="rounded-none">new</SelectItem>
                        <SelectItem value="contacted" className="rounded-none">contacted</SelectItem>
                        <SelectItem value="closed" className="rounded-none">closed</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      data-testid={`delete-${q.id}`}
                      onClick={() => remove(q.id)}
                      className="p-2 border border-[#27272a] hover:border-red-500 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-4 gap-4 mb-4">
                  <Info label="Usługa" value={q.service || "—"} />
                  <Info
                    label="Data"
                    value={new Date(q.created_at).toLocaleString("pl-PL")}
                  />
                  <Info label="ID" value={q.id.slice(0, 8)} mono />
                </div>

                <div className="border-t border-[#1f1f1f] pt-4">
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#71717a] mb-2">
                    Wiadomość
                  </p>
                  <p className="text-sm text-[#d4d4d8] whitespace-pre-wrap leading-relaxed">
                    {q.message}
                  </p>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

const Info = ({ label, value, mono }) => (
  <div>
    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#71717a] mb-1">
      {label}
    </p>
    <p className={`text-sm text-white ${mono ? "font-mono" : ""}`}>{value}</p>
  </div>
);

export default Admin;
